/**************************************************************************************************/
/** @import Node Packages                                                                        **/
/**************************************************************************************************/
import { defineComponent } from 'vue'

/**************************************************************************************************/
/** @import Helpers/Utils                                                                        **/
/**************************************************************************************************/
import { PrintHelper } from '@/Helpers/Utils/PrintHelper'

/**************************************************************************************************/
/** @import Interfaces/Global                                                                    **/
/**************************************************************************************************/
import { Components } from '@/Interfaces/Global/Components'

/**************************************************************************************************/
/** @import Mixins                                                                               **/
/**************************************************************************************************/
import MixinComponent from '@/Mixins/MixinComponent'
import MixinResponsive from '@/Mixins/MixinResponsive'

/**************************************************************************************************/
/** @import Store/Global                                                                         **/
/**************************************************************************************************/
import Store from '@/Store/Global/Default'

/**************************************************************************************************/
/** @internal Const @name SideMenu                                                               **/
/**************************************************************************************************/
const SideMenu = defineComponent(
{
	/*************************************************/
	/** @Properties                                 **/
	/*************************************************/
	name: 'SideMenu', mixins: [MixinComponent, MixinResponsive],

	/*************************************************/
	/** @Data                                       **/
	/*************************************************/
	data()
	{
		return {
			items: [] as Components.Global.SideMenu.Default['items'],
			states:
			{
				isMobile: false
			}
		}
	},

	/*************************************************/
	/** @Computed                                   **/
	/*************************************************/
	computed:
	{
		GetSideMenuClass() : string | object
		{
			if (this.states.isMobile) return this.GetResponsiveClasses
			return Store.getters.getStoredSideMenu.class
		},

		GetSideMenuIsMinimized() : boolean
		{
			return Store.getters.getStoredSideMenu.isMinimized
		}
	},

	/*************************************************/
	/** @Methods                                    **/
	/*************************************************/
	methods:
	{
		ClearSelection() : void
		{
			for (const item of this.items)
			{
				if (Array.isArray(item.items))
				{
					for (const subItem of item.items) subItem.isActive = false
				}
				item.isActive = false
			}
		},

		GetToggleIcon() : string
		{
			return this.GetSideMenuIsMinimized ? 'arrow-alt-circle-right' : 'arrow-alt-circle-left'
		},

		Initialize(sideMenu: Components.Global.SideMenu.Default) : void
		{
			const { items } = sideMenu
			this.items = items
		},

		OnButtonClick(item: Components.Global.SideMenu.Item) : void
		{
			PrintHelper.warning('[SideMenu]:onButtonClick event triggered')
			this.UpdateActiveItem(item)
			this.$emit('onButtonClick', item?.componentType, item.componentName)
		},

		OnSMSubItemClick(item: Components.Global.SideMenu.Item, subItem: Components.Global.SideMenu.SubItem) : void
		{
			PrintHelper.warning('[SideMenu]:onSMSubItemClick event triggered')
			this.UpdateSubActiveItem(item, subItem)
			this.$emit('onSMSubItemClick', item.componentName, subItem.name)
		},

		OnSMToggleClick() : void
		{
			PrintHelper.warning('[SideMenu]:onSMToggleClick event triggered')
			Store.commit('updateSideMenuIsMinimized', !this.GetSideMenuIsMinimized)
		},

		UpdateActiveItem(item: Components.Global.SideMenu.Item) : void
		{
			this.ClearSelection()
			item.isActive = true
			if (Array.isArray(item.items) && item.items.length > 0) item.items[0].isActive = true
		},

		UpdateSubActiveItem(item: Components.Global.SideMenu.Item, subItem: Components.Global.SideMenu.SubItem) : void
		{
			this.ClearSelection()
			item.isActive = true
			subItem.isActive = true
		}
	}
})

/**************************************************************************************************/
/** @export Component @name SideMenu                                                             **/
/**************************************************************************************************/
export default SideMenu

/**************************************************************************************************/
/** @export Type @name SideMenuRef                                                               **/
/**************************************************************************************************/
export type SideMenuRef = InstanceType<typeof SideMenu>